/**
 * Created by Nikolai Gorte
 * User: n.gorte on 05.09.2016
 * Copyright: xdot GmbH 2016
 */

import React from 'external/react';
import PropTypes from 'external/prop-types';
import { DragDropContext, Draggable, Droppable } from 'external/react-beautiful-dnd';

const grid = 8;
const getItemStyle = (isDragging, draggableStyle, active) => ({
  userSelect: 'none',
  margin: `0 0 ${grid}px 0`,
  padding: grid * 2,
  boxShadow: 'rgba(0, 0, 0, 0.2) 0px 1px 2px 0px',
  background: isDragging ? '#fbfbbc' : active ? '#eaeaea' : 'white',
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  ...draggableStyle
});

const getScrollableStyles = (isScrollable, maxHeight) => {
  if (!isScrollable) {
    return { maxHeight: 'none' };
  }
  if (maxHeight === 'small') {
    return { maxHeight: 340, overflowY: 'auto' };
  }
  if (maxHeight === 'large') {
    return { maxHeight: 740, overflowY: 'auto' };
  }
  return { maxHeight: 600, overflowY: 'auto' };
};

const getListStyle = (isScrollable, maxHeight) => ({
  background: 'transparent',
  padding: grid,
  ...getScrollableStyles(isScrollable, maxHeight)
});

function reorder(list, startIndex, endIndex) {
  let result = Array.from(list);
  const [ removed ] = result.splice(startIndex, 1);
  result.splice(endIndex, 0, removed);
  return result;
}

function DraggableList({
  changeOrder,
  DragItem,
  list,
  onEditElement,
  onDeleteElement,
  onClick,
  maxHeight,
  isScrollable,
  keyProp = 'id',
  active,
}) {

  const onDragEnd = (result) => {
    if (!result.destination) {
      return;
    }
    const items = reorder(
      list,
      result.source.index,
      result.destination.index
    );
    changeOrder(items);
  };

  return (
    <DragDropContext onDragEnd={onDragEnd}>
      <Droppable droppableId="droppable">
        {
          (provided, snapshot) => (
            <div
              {...provided.droppableProps}
              ref={provided.innerRef}
              style={getListStyle(isScrollable, maxHeight)}
            >
              {
                list && list.map((item, index) => (
                  <Draggable
                    key={item[keyProp]}
                    draggableId={(item[keyProp] && item[keyProp].toString()) || index.toString()}
                    index={index}
                  >
                    {(provided, snapshot) => (
                      <div
                        ref={provided.innerRef}
                        {...provided.draggableProps}
                        {...provided.dragHandleProps}
                        style={getItemStyle(
                          snapshot.isDragging,
                          provided.draggableProps.style,
                          active && active[keyProp] === item[keyProp],
                        )}
                        onClick={() => onClick && onClick(item)}
                      >
                        <i className="fa fa-sort mr-3"/>
                        <DragItem
                          item={item}
                          onEdit={onEditElement && onEditElement}
                          onDelete={onDeleteElement && onDeleteElement}
                          position={index}
                        />
                      </div>
                    )}
                  </Draggable>
                ))
              }
            </div>
          )
        }
      </Droppable>
    </DragDropContext>
  );

}

DraggableList.propTypes = {
  list: PropTypes.array,
  changeOrder: PropTypes.func,
  onClick: PropTypes.func,
  onDeleteElement: PropTypes.func,
  onEditElement: PropTypes.func,
  DragItem: PropTypes.func,
  active: PropTypes.object,
  itemHeight: PropTypes.number,
  margin: PropTypes.number,
  isScrollable: PropTypes.bool,
  maxHeight: PropTypes.oneOf([ 'none', 'small', 'medium', 'large' ]),
  keyProp: PropTypes.string,
};

export default DraggableList;



// WEBPACK FOOTER //
// ./js/components/DraggableList.jsx